import { memo, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { DividerLine } from 'common-components/DividerLine'
import { T } from 'consts/translationKeys'
import { dispatch } from 'store'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { aiLibImageSelectors } from 'store/features/aiLibImage/selectors'
import { aiLibImageThunks } from 'store/features/aiLibImage/thunks'
import { TAiImageType } from 'store/types/aiLibImage'
import { replaceTranslationVariables } from 'utils/text-transformation'
import { translate } from 'utils/translations'
import * as Styled from './styled'
import HistoryScopeCheckbox from '../HistoryCheckbox'
import { IDropdownOption, TDropdownValue } from './types'
import { FilterDropdown } from './FilterDropdown'

const FiltersSection = () => {
  const isLoading = useSelector(aiLibImageSelectors.isInitialLoading)
  const isHistoryScopedToCurrentProject = useSelector(
    aiLibImageSelectors.isHistoryScopedToCurrentProject
  )
  const selectedModelCode = useSelector(aiLibImageSelectors.selectedModelCode)
  const selectedAspectRatio = useSelector(
    aiLibImageSelectors.selectedAspectRatio
  )
  const selectedStyleId = useSelector(aiLibImageSelectors.selectedStyleId)
  const selectedImageType = useSelector(aiLibImageSelectors.selectedImageType)

  const imageModels = useSelector(aiImageSelectors.modelsData)
  const imageStyles = useSelector(aiImageSelectors.stylesData)

  const modelOptions = useMemo<IDropdownOption[]>(
    () =>
      imageModels.map(model => ({
        value: model.code,
        label: model.name
      })),
    [imageModels]
  )

  const aspectRatioOptions = useMemo<IDropdownOption[]>(() => {
    const selectedModel = selectedModelCode
      ? imageModels.find(model => model.code === selectedModelCode)
      : null

    const aspectRatios = selectedModel
      ? selectedModel.possibleAspectRatios
      : Array.from(
          new Set(imageModels.flatMap(model => model.possibleAspectRatios))
        )

    return aspectRatios.map(aspectRatio => ({
      value: aspectRatio,
      label: aspectRatio
    }))
  }, [imageModels, selectedModelCode])

  const styleOptions = useMemo<IDropdownOption[]>(
    () =>
      imageStyles.map(style => ({
        value: style.id,
        label: style.name
      })),
    [imageStyles]
  )

  const imageTypeOptions = useMemo<IDropdownOption[]>(
    () => [{ value: 'character', label: translate(T.CHARACTERS_LABEL) }],
    []
  )

  const onToggleScope = useCallback((next: boolean) => {
    dispatch(
      aiLibImageThunks.setFilter({ isHistoryScopedToCurrentProject: next })
    )
  }, [])

  const handleModelChange = useCallback((value: TDropdownValue) => {
    dispatch(
      aiLibImageThunks.changeModel(value === null ? null : String(value))
    )
  }, [])

  const handleAspectRatioChange = useCallback((value: TDropdownValue) => {
    dispatch(
      aiLibImageThunks.setFilter({
        aspectRatio: value === null ? null : String(value)
      })
    )
  }, [])

  const handleStyleChange = useCallback((value: TDropdownValue) => {
    dispatch(
      aiLibImageThunks.setFilter({
        styleId: value === null ? null : Number(value)
      })
    )
  }, [])

  const handleImageTypeChange = useCallback((value: TDropdownValue) => {
    dispatch(
      aiLibImageThunks.setFilter({
        imageType: value === null ? null : (value as TAiImageType)
      })
    )
  }, [])

  const historyScopeLabel = useMemo(
    () =>
      replaceTranslationVariables(translate(T.CREATED_IN_THIS_PROJECT_LABEL), [
        translate(T.IMAGES_LABEL)
      ]),
    []
  )

  return (
    <Styled.ScopeCheckboxWrapper>
      <Styled.ModelSection>
        <Styled.DropdownWrapper>
          <FilterDropdown
            className="model-dropdown"
            label={translate(T.MODEL_LABEL)}
            options={modelOptions}
            selectedValue={selectedModelCode}
            onSelect={handleModelChange}
          />

          <Styled.ModelOptionsWrapper>
            <FilterDropdown
              className="option-dropdown"
              label={translate(T.ASPECT_RATIO_LABEL)}
              options={aspectRatioOptions}
              selectedValue={selectedAspectRatio}
              onSelect={handleAspectRatioChange}
            />

            <FilterDropdown
              className="option-dropdown"
              label={translate(T.STYLES_LABEL)}
              options={styleOptions}
              selectedValue={selectedStyleId}
              onSelect={handleStyleChange}
            />

            <FilterDropdown
              className="option-dropdown"
              label={translate(T.TYPE_LABEL)}
              options={imageTypeOptions}
              selectedValue={selectedImageType}
              onSelect={handleImageTypeChange}
            />
          </Styled.ModelOptionsWrapper>
        </Styled.DropdownWrapper>
      </Styled.ModelSection>

      <HistoryScopeCheckbox
        className="scope-checkbox-label"
        label={historyScopeLabel}
        isChecked={isHistoryScopedToCurrentProject}
        onToggle={onToggleScope}
        isDisabled={isLoading}
      />
      <DividerLine />
    </Styled.ScopeCheckboxWrapper>
  )
}

export default memo(FiltersSection)
